import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import toastr from 'toastr';
import { fetchMedications, updateMedication, createMedication } from '../../../services/clinicPortalServices/medicationService';

import PageHeader from '../../common/PageHeader';
const toList = (s) => [...new Set(String(s || '').split(',').map((a) => a.trim().toLowerCase()).filter(Boolean))];
const chip = { display: 'inline-block', padding: '2px 10px', margin: '2px 4px 2px 0', borderRadius: 12, background: '#eef2f7', color: '#334155', fontSize: 12 };

export default function Medications() {
    const [meds, setMeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [showUnnamed, setShowUnnamed] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editVal, setEditVal] = useState('');
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);
    const [newMed, setNewMed] = useState({ name: '', description: '', aliases: '' });

    const load = () => {
        setLoading(true);
        fetchMedications()
            .then((res) => setMeds(((res && res.data) || []).slice().sort((a, b) => String(a.name || '~').localeCompare(String(b.name || '~')))))
            .catch(() => toastr.error('Could not load medications'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const unnamed = meds.filter((m) => !String(m.name || '').trim());
    const shown = useMemo(() => {
        const s = q.toLowerCase().trim();
        return meds
            .filter((m) => showUnnamed || String(m.name || '').trim())
            .filter((m) => !s || String(m.name || '').toLowerCase().includes(s) || (m.aliases || []).some((a) => String(a).toLowerCase().includes(s)));
    }, [meds, q, showUnnamed]);

    const saveAliases = (m) => {
        const aliases = toList(editVal);
        setSaving(true);
        updateMedication({ ...m, aliases })
            .then((res) => {
                if (res && res.success === false) { toastr.error(res.message || 'Save failed'); return; }
                setMeds(meds.map((x) => (x._id === m._id ? { ...x, aliases } : x)));
                setEditId(null);
                toastr.success(`Aliases saved for ${m.name}`);
            })
            .catch(() => toastr.error('Save failed'))
            .finally(() => setSaving(false));
    };

    const addMedication = () => {
        const name = newMed.name.trim();
        if (!name) { toastr.error('Medication name is required'); return; }
        if (meds.some((m) => String(m.name || '').trim().toLowerCase() === name.toLowerCase())) { toastr.warning(`"${name}" already exists`); return; }
        setSaving(true);
        createMedication({ name, description: newMed.description.trim(), aliases: toList(newMed.aliases) })
            .then((res) => {
                if (res && res.success === false) { toastr.error(res.message || 'Could not add medication'); return; }
                toastr.success(`${name} added`);
                setAdding(false);
                setNewMed({ name: '', description: '', aliases: '' });
                load();
            })
            .catch(() => toastr.error('Could not add medication'))
            .finally(() => setSaving(false));
    };

    return (
        <div style={{ width: '100%', flex: 1 }}>
            <PageHeader section="Administration" title="Medications" />
            <div className="clinic-contain bl-page" style={{ padding: 20 }}>
            <p className="text-muted">
                Aliases are brand, generic and street names for the same medication (e.g. Percocet, OxyContin, "percs" for Oxycodone).
                They are used when ordering and when Tox Review compares results with a patient's prescriptions, so free-text entries still count.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
                <Form.Control style={{ maxWidth: 320 }} placeholder="Search name or alias…" value={q} onChange={(e) => setQ(e.target.value)} />
                <span className="text-muted">{shown.length} shown</span>
                {unnamed.length > 0 && (
                    <Form.Check type="switch" id="med-show-unnamed" label={`Show ${unnamed.length} unnamed record${unnamed.length > 1 ? 's' : ''}`} checked={showUnnamed} onChange={(e) => setShowUnnamed(e.target.checked)} />
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <Button variant="outline-dark" size="sm" onClick={load}>Refresh</Button>
                    <Button variant="success" size="sm" onClick={() => setAdding(true)}><i className="fas fa-plus mr-1"></i>Add Medication</Button>
                </div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
                <table className="table table-sm mb-0 bl-table">
                    <thead>
                        <tr>
                            <th style={{ width: '30%', position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Medication</th>
                            <th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Aliases</th>
                            <th style={{ width: 150, position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="3" className="text-center p-4">Loading…</td></tr>}
                        {!loading && !shown.length && <tr><td colSpan="3" className="text-center p-4">No medications found.</td></tr>}
                        {!loading && shown.map((m) => (
                            <tr key={m._id} style={{ verticalAlign: 'middle' }}>
                                <td>
                                    <div style={{ fontWeight: 600 }}>{String(m.name || '').trim() || <span className="text-muted fst-italic">(no name)</span>}</div>
                                    {m.description ? <small className="text-muted">{m.description}</small> : null}
                                </td>
                                <td>
                                    {editId === m._id ? (
                                        <Form.Control size="sm" autoFocus value={editVal} placeholder="comma-separated, e.g. norco, vicodin" onChange={(e) => setEditVal(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') saveAliases(m); if (e.key === 'Escape') setEditId(null); }} />
                                    ) : (m.aliases || []).length ? (
                                        (m.aliases || []).map((a) => <span key={a} style={chip}>{a}</span>)
                                    ) : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                                    {editId === m._id ? (
                                        <>
                                            <Button size="sm" variant="success" disabled={saving} onClick={() => saveAliases(m)}>{saving ? 'Saving…' : 'Save'}</Button>{' '}
                                            <Button size="sm" variant="outline-secondary" disabled={saving} onClick={() => setEditId(null)}>Cancel</Button>
                                        </>
                                    ) : (
                                        <Button size="sm" variant="outline-dark" onClick={() => { setEditId(m._id); setEditVal((m.aliases || []).join(', ')); }}>
                                            <i className="fas fa-edit mr-1"></i>Edit aliases
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Modal show={adding} onHide={() => !saving && setAdding(false)} centered>
                <Modal.Header closeButton><Modal.Title>Add Medication</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control autoFocus value={newMed.name} placeholder="e.g. Buprenorphine (Suboxone)" onChange={(e) => setNewMed({ ...newMed, name: e.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control value={newMed.description} onChange={(e) => setNewMed({ ...newMed, description: e.target.value })} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Aliases</Form.Label>
                        <Form.Control value={newMed.aliases} placeholder="comma-separated, e.g. suboxone, subutex, subs" onChange={(e) => setNewMed({ ...newMed, aliases: e.target.value })} />
                        <small className="text-muted">To use this medication in Tox Review, link it to its analytes in Lab Hub → analyte settings.</small>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" disabled={saving} onClick={() => setAdding(false)}>Cancel</Button>
                    <Button variant="success" disabled={saving} onClick={addMedication}>{saving ? 'Adding…' : 'Add'}</Button>
                </Modal.Footer>
            </Modal>
            </div>
        </div>
    );
}
